import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/assets";
import { Check } from "lucide-react";

const easeOut = [0.16, 1, 0.3, 1];

export default function About() {
  const { t } = useLang();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0B] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: easeOut }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-[#121214] group">
              <img
                src={IMAGES.about}
                alt="Industrial pipelines"
                className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent opacity-60" />
              {/* corner markers */}
              <div className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#D4AF37]" />
              <div className="absolute top-4 right-4 w-6 h-6 border-t border-r border-[#D4AF37]" />
              <div className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-[#D4AF37]" />
              <div className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[#D4AF37]" />
            </div>

            <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] text-black px-6 py-5 hidden lg:block">
              <div className="font-display font-black text-3xl leading-none">18</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] mt-1">
                years of practice
              </div>
            </div>
          </motion.div>

          {/* Right: text */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: easeOut, delay: 0.1 }}
            className="lg:col-span-7 flex flex-col"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{t.about.eyebrow}</span>
            </div>

            <h2 className="font-display font-bold text-[clamp(2rem,4vw,3.5rem)] text-white tracking-tight leading-[1.05] max-w-[18ch]">
              {t.about.title}
            </h2>

            <div className="mt-8 space-y-5 text-[#A1A1AA] text-base lg:text-lg leading-relaxed max-w-2xl">
              <p>{t.about.p1}</p>
              <p>{t.about.p2}</p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 max-w-3xl">
              {t.about.pillars.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.1 * i, ease: easeOut }}
                  data-testid={`about-pillar-${i}`}
                  className="flex gap-3"
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-5 h-5 border border-[#D4AF37] flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#D4AF37]" />
                    </div>
                  </div>
                  <div>
                    <div className="font-display font-semibold text-white text-[15px]">
                      {p.k}
                    </div>
                    <div className="text-sm text-[#8B939C] mt-1 leading-relaxed">
                      {p.v}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
